import { authStoreUserTournamentId } from 'modules/auth/store/selectors';
import { ListChampionshipsArgs, useListChampionshipsQuery } from 'modules/tournaments/store/tournaments/championships-api';
import { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { normalizeRtkqError } from 'shared/utils';
import { useBlockPagerCore } from 'shared/utils/pagination';
import { NumberParam, StringParam, useQueryParams, withDefault } from 'use-query-params';

// Состояние «спекулятивных страниц» хранится централизованно в shared/utils/pagination/speculative-pages.ts

const defaultFilters = {
  search: StringParam,
  page: withDefault(NumberParam, 1),
  perPage: withDefault(NumberParam, 9),
  year: withDefault(NumberParam, null),
};

export const useTourneysList = () => {
  const tournamentId = useSelector(authStoreUserTournamentId);
  const [filters, setFilters] = useQueryParams(defaultFilters);

  // Дебаунс для пользовательских изменений (например, поиск). ВНИМАНИЕ: не используем захваченный filters
  const changeFilter = useCallback(
    (changes: Partial<typeof filters>) => {
      // setFilters из use-query-params не поддерживает функциональный апдейтер,
      // поэтому сливаем с актуальным снимком фильтров в момент вызова.
      // Для клампа используем отдельный немедленный сеттер ниже.
      setFilters({ ...(changes as any) });
    },
    [setFilters],
  );

  // Немедленный сеттер без debounce — используется для клампа, чтобы избежать гонок
  const applyFilterImmediately = useCallback(
    (changes: Partial<typeof filters>) => {
      // Немедленный апдейт фильтров без дебаунса.
      // Второй аргумент UrlUpdateType: используем 'replace', чтобы не засорять историю при клампе.
      setFilters({ ...(changes as any) }, 'replace');
    },
    [setFilters],
  );

  const page = filters.page ?? 1;
  const perPage = filters.perPage ?? 9;

  // Переход на универсальный пейджер поверх RTK Query
  const pager = useBlockPagerCore<
    import('modules/tournaments/domain/interfaces/responce-dto').Championship,
    ListChampionshipsArgs,
    typeof filters
  >({
    useQueryHook: useListChampionshipsQuery,
    page,
    perPage,
    filters,
    baseArgs: { tournamentId: Number(tournamentId) },
    mapFiltersToArgs: (f) => ({
      namePart: (f?.search ?? undefined) as string | undefined,
      // year/afterDate/beforeDate можно добавить здесь по необходимости
    }),
    rawPrefetchPages: 3,
    maxBackendLimit: 20,
    scopeEntity: 'championships',
    scopeParts: { tournamentId },
    getQueryOptions: () => ({ skip: !tournamentId }),
  });

  const tournaments = pager.itemsForPage;
  const pages = pager.pages;
  const isLoading = pager.isLoading;
  const isError = pager.isError;
  const errorMessage = normalizeRtkqError(pager.error);

  // Кламп при подтверждённом конце данных (после загрузки)
  useEffect(() => {
    if (isLoading) return;
    if (pager.spec.provenNoMore && page > pager.spec.maxKnownPages) {
      applyFilterImmediately({ page: pager.spec.maxKnownPages } as any);
    }
  }, [isLoading, pager.spec, page, applyFilterImmediately]);

  return {
    changeFilter,
    filters: { ...filters, page, perPage },
    tourneys: tournaments,
    years: [],
    isLoading,
    isError,
    errorMessage,
    pages,
  };
};
